import React from 'react';
import {
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  FilterOutlined,
  LinkOutlined,
  ProfileOutlined,
  RiseOutlined,
  ShoppingOutlined,
  SyncOutlined,
  TeamOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';

/**
 * The glyph a context chip carries, one per chip kind, drawn from the same family the dashboard's
 * left navigation uses: `components/Header/MenuIcon.tsx` inlines antd glyphs for its own items, and
 * its Intelligence Suite icon (`MenuIcon.tsx` `src === 'intelligence_suite'`) is antd's `rise` path
 * verbatim — the same path `RiseOutlined` draws — so the `screen` chip below is that nav item's own
 * icon rather than a lookalike.
 *
 * Keyed by the chip's own id: `screen` and `card` are Orbit's, every other key is a published
 * `ScreenFilterChip.id` from the two Intelligence filter signatures. PLA-8844.
 */
const CHIP_ICONS: Record<string, typeof RiseOutlined> = {
  screen: RiseOutlined,
  card: BarChartOutlined,
  period: CalendarOutlined,
  lifecycle: SyncOutlined,
  purchaseBehaviour: ShoppingOutlined,
  customerValue: DollarOutlined,
  engagement: ThunderboltOutlined,
  productRelationship: LinkOutlined,
  demographics: TeamOutlined,
  consentStatus: CheckCircleOutlined,
  activityType: ProfileOutlined,
  productHolding: BankOutlined
};

export interface OrbitChipIconProps {
  /** The chip's own id. An id with no entry above falls back to the generic filter glyph. */
  kind: string;
}

/**
 * The icon a context chip opens with. Monochrome: antd draws every glyph with `fill="currentColor"`,
 * and `OrbitChip` sizes and spaces it, so it takes the chip's own ink at the chip's text size.
 *
 * Hidden from assistive technology because the chip's text already names the category — "Period:
 * Last 7 days" — so the glyph adds nothing a reader has not been told.
 */
const OrbitChipIcon: React.FC<OrbitChipIconProps> = ({ kind }) => {
  const Icon = CHIP_ICONS[kind] ?? FilterOutlined;

  return <Icon aria-hidden="true" data-testid={`orbit-chip-icon-${kind}`} />;
};

export default OrbitChipIcon;
